import { useState, useEffect, useCallback, Dispatch, SetStateAction } from 'react';

/**
 * A hook that syncs state with localStorage, acting like useState but persisting values.
 *
 * @param key - The localStorage key to use for persistence
 * @param defaultValue - The default value if no stored value exists
 * @returns A stateful value and a function to update it, just like useState
 *
 * @example
 * const [name, setName] = useLocalStorageState('userName', 'Anonymous');
 * const [count, setCount] = useLocalStorageState('counter', 0);
 * const [user, setUser] = useLocalStorageState('user', { id: 1, name: 'John' });
 */
export function useLocalStorageState<T>(
  key: string,
  defaultValue: T
): [T, Dispatch<SetStateAction<T>>] {
  // Initialize state with value from localStorage or defaultValue
  const [state, setState] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      if (item === null) {
        return defaultValue;
      }
      return JSON.parse(item) as T;
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error);
      return defaultValue;
    }
  });

  // Wrap setState to also update localStorage
  const setLocalStorageState = useCallback<Dispatch<SetStateAction<T>>>(
    (value) => {
      try {
        setState((prevState) => {
          // Handle functional updates
          const newValue = value instanceof Function ? value(prevState) : value;

          // Save to localStorage
          window.localStorage.setItem(key, JSON.stringify(newValue));

          return newValue;
        });
      } catch (error) {
        console.warn(`Error setting localStorage key "${key}":`, error);
      }
    },
    [key]
  );

  // Sync state when localStorage changes in other tabs/windows
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key && e.newValue !== null) {
        try {
          setState(JSON.parse(e.newValue) as T);
        } catch (error) {
          console.warn(`Error parsing storage event for key "${key}":`, error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [key]);

  return [state, setLocalStorageState];
}
